import React, { Suspense, lazy } from "react";
import { createBrowserRouter, RouterProvider } from "react-router";

import GuestRoute from "./GuestRoute";
import AuthRoute from "./AuthRoute";
import AdminRoute from "./AdminRoute";

import { store } from "../Store/Store";
import { Provider } from "react-redux";

import CookieBanner from "../Pages/Component/CookieBanner.js";

// ── Eager: the paid-acquisition landing pages ─────────────────────────
// /dc and /nyc are where Google Ads traffic lands, so they stay in the
// main bundle — a lazy chunk here would put an extra round trip in front
// of the hero on exactly the pages that can least afford it.
import LandingDC from "../Pages/Landing/DC.js";
import LandingNYC from "../Pages/Landing/NYC.js";

// ── Lazy: everything else ─────────────────────────────────────────────
// The rest of the app — admin tables, the booking flow, Stripe, the Maps
// SDK, react-datepicker, slick — was all in the single main bundle, so a
// visitor landing on /dc downloaded and parsed the entire authenticated
// application before the hero could paint. None of it is reachable from
// a landing page without a navigation, so it splits cleanly.
const Login = lazy(() => import("../Pages/Auth/Login"));
const Signup = lazy(() => import("../Pages/Auth/Signup"));
const SpotDetails = lazy(() => import("../Pages/SpotDetails"));
const UsersList = lazy(() => import("../Pages/Admin/UsersList.js"));
const SpotsList = lazy(() => import("../Pages/Admin/SpotsList.js"));
const PaymentsList = lazy(() => import("../Pages/Admin/PaymentsList.js"));
const VehiclesList = lazy(() => import("../Pages/Admin/VehiclesList.js"));
const AppVersionSettings = lazy(() =>
  import("../Pages/Admin/AppVersionSettings.js"),
);

const UserSpotsList = lazy(() => import("../Pages/User/SpotsList.js"));
const UserPaymentsList = lazy(() => import("../Pages/User/PaymentsList.js"));
const UserVehiclesList = lazy(() => import("../Pages/User/VehiclesList.js"));
const SpotBooking = lazy(() => import("../Pages/SpotBooking.js"));
const BookingDetails = lazy(() => import("../Pages/BookingDetails.js"));
const BookingsList = lazy(() => import("../Pages/Admin/BookingsList.js"));
const UserBookingsList = lazy(() => import("../Pages/User/BookingsList.js"));
const UserRequestsList = lazy(() => import("../Pages/User/RequestsList.js"));
const UserProfile = lazy(() => import("../Pages/User/Profile.js"));
const NotFound = lazy(() => import("../Pages/NotFound.js"));

const AppRoutes = () => {
  const router = createBrowserRouter([
    { path: "/", element: <GuestRoute component={Login} /> },
    { path: "/login", element: <GuestRoute component={Login} /> },
    { path: "/signup", element: <GuestRoute component={Signup} /> },
    { path: "/admin/", element: <AdminRoute component={SpotsList} /> },
    { path: "/admin/spots", element: <AdminRoute component={SpotsList} /> },
    { path: "/admin/spots/page/:page", element: <SpotsList /> },
    // { path: "/admin/users", element: <AdminRoute component={UsersList} /> },
    { path: "/admin/users", element: <AdminRoute component={UsersList} /> },
    {
      path: "/admin/users/page/:page",
      element: <AdminRoute component={UsersList} />,
    },
    {
      path: "/admin/payments",
      element: <AdminRoute component={PaymentsList} />,
    },
    {
      path: "/admin/payments/page/:page",
      element: <AdminRoute component={PaymentsList} />,
    },
    {
      path: "/admin/vehicles",
      element: <AdminRoute component={VehiclesList} />,
    },
    {
      path: "/admin/vehicles/page/:page",
      element: <AdminRoute component={VehiclesList} />,
    },
    {
      path: "/admin/bookings",
      element: <AdminRoute component={BookingsList} />,
    },
    {
      path: "/admin/bookings/page/:page",
      element: <AdminRoute component={BookingsList} />,
    },
    {
      path: "/admin/app-versions",
      element: <AdminRoute component={AppVersionSettings} />,
    },

    { path: "/user", element: <AuthRoute component={UserSpotsList} /> },
    { path: "/user/spots", element: <AuthRoute component={UserSpotsList} /> },
    {
      path: "/user/spots/page/:page",
      element: <AuthRoute component={UserSpotsList} />,
    },
    {
      path: "/user/payments",
      element: <AuthRoute component={UserPaymentsList} />,
    },
    {
      path: "/user/payments/page/:page",
      element: <AuthRoute component={UserPaymentsList} />,
    },
    {
      path: "/user/vehicles",
      element: <AuthRoute component={UserVehiclesList} />,
    },
    {
      path: "/user/vehicles/page/:page",
      element: <AuthRoute component={UserVehiclesList} />,
    },
    {
      path: "/user/bookings",
      element: <AuthRoute component={UserBookingsList} />,
    },
    {
      path: "/user/bookings/page/:page",
      element: <AuthRoute component={UserBookingsList} />,
    },
    {
      path: "/user/requests",
      element: <AuthRoute component={UserRequestsList} />,
    },
    {
      path: "/user/profile",
      element: <AuthRoute component={UserProfile} />,
    },

    { path: "/spot/:spotId", element: <GuestRoute component={SpotDetails} /> },
    {
      path: "/booking/:spotId",
      element: <GuestRoute component={SpotBooking} />,
    },
    {
      path: "/active-booking/:bookingId",
      element: <GuestRoute component={BookingDetails} />,
    },
    { path: "/dc", element: <LandingDC /> },
    { path: "/nyc", element: <LandingNYC /> },

    { path: "*", element: <NotFound /> },
  ]);

  return (
    <Provider store={store}>
      {/* One boundary around the router covers every lazy route. The
          landing pages are eager, so they never hit this fallback. */}
      <Suspense fallback={null}>
        <RouterProvider router={router} />
      </Suspense>
      {/* Outside Suspense: the consent prompt must not wait on a route
          chunk to decide whether to show itself. */}
      <CookieBanner />
    </Provider>
  );
};

export default AppRoutes;
